import { useState, useCallback, useRef } from 'react';
import { openAI } from '../../services/openAIService';
import { AIIntent, AIIntentResponse } from './types';

interface UseAIIntentReturn {
  intent: AIIntent | null;
  isLoading: boolean;
  getIntent: (transcript: string) => Promise<AIIntent | null>;
  clearIntent: () => void;
}

// System prompt for AI intent parsing (schedule_availability + navigation)
const SYSTEM_PROMPT = `Antwoord ALLEEN in JSON format:
{"type": "TYPE", "message"/"target"/"tab"/"payload": "VALUE"}

Types:
- respond: Antwoord geven (message)
- navigate: Naar pagina (target: dashboard|connecties|agenda|inbox|mijn-verhaal|vibes)
- open_newsfeed: Nieuwsfeed modal openen
- open_inbox_tab: Inbox tab (tab: unread|reminders|planning|summaries|confirmations|chat|connection_requests|podcast_invites)
- open_connectieflow: ConnectieFlow chat
- show_updates: Dagelijkse updates
- connectieflow_call/schedule/pause: CF acties
- explain: Uitleg (topic)
- ask_clarify: Verduidelijking (question)
- schedule_availability: Beschikbaar blok plannen in agenda
  payload velden (allemaal optioneel, defaults worden toegepast):
    title (default "Beschikbaar")
    description (originele transcript wordt automatisch toegevoegd)
    date (yyyy-mm-dd, indien niet genoemd: vandaag)
    startTime (HH:mm, default 14:00)
    endTime (HH:mm, default 15:00)
    repeat (none|daily|weekly|monthly, default none)
    repeatEndDate (yyyy-mm-dd, alleen bij repeat != none)
    reminderEnabled (bool, default true)
    reminderMinutesBefore (number, default 60)
    locationPreference (auto_midpoint|at_my_office|at_other_office|selected_location, default auto_midpoint)
    locationDetail (string, alleen bij specific)
- idle: Geen actie

Mapping:
- home/thuispagina/hoofdpagina → dashboard
- profiel/verhaal → mijn-verhaal
- nieuwsfeed/feed/nieuws → open_newsfeed
- ongelezen → unread
- bel/bellen → connectieflow_call
- plan/afspraak → connectieflow_schedule
- pauze/later → connectieflow_pause
- beschikbaar/beschikbaarheid/zet me beschikbaar → schedule_availability
- bij mij/op mijn kantoor → my_office
- bij de ander/bij hem/bij haar → other_office
- automatisch/halverwege → auto_midpoint
- elke dag/dagelijks → daily
- elke week/wekelijks → weekly
- elke maand/maandelijks → monthly
- reminder X uur → reminderMinutesBefore: X * 60
- reminder X minuten → reminderMinutesBefore: X

Voorbeelden:
"ga naar dashboard" → {"type":"navigate","target":"dashboard"}
"open nieuwsfeed" → {"type":"open_newsfeed"}
"wat kan ik doen" → {"type":"respond","message":"Je kunt netwerken, agenda beheren, berichten bekijken"}
"bel direct" → {"type":"connectieflow_call"}
"zet me beschikbaar" → {"type":"schedule_availability","payload":{}}
"zet me beschikbaar morgen om 10 uur" → {"type":"schedule_availability","payload":{"date":"[morgen datum]","startTime":"10:00","endTime":"11:00"}}
"zet me beschikbaar op 3 januari van 13:00 tot 14:00, elke week tot 31 maart 2026, bij mij, reminder 1 uur" →
{"type":"schedule_availability","payload":{"date":"2026-01-03","startTime":"13:00","endTime":"14:00","repeat":"weekly","repeatEndDate":"2026-03-31","reminderMinutesBefore":60,"locationPreference":"at_my_office"}}
"beschikbaar van 9 tot 12 bij de ander" → {"type":"schedule_availability","payload":{"startTime":"09:00","endTime":"12:00","locationPreference":"at_other_office"}}

ALLEEN JSON, geen tekst!`;

function validateIntent(data: any): AIIntent | null {
  if (!data || typeof data !== 'object' || !data.type) {
    return null;
  }

  switch (data.type) {
    case 'respond':
      if (typeof data.message === 'string') {
        return { type: 'respond', message: data.message };
      }
      break;
    case 'navigate':
      if (typeof data.target === 'string') {
        let target = data.target;
        if (target === 'feeds' || target === 'feed' || target === 'nieuwsfeed' || target === 'newsfeed') {
          console.log('🔄 Mapping', target, '→ dashboard');
          target = 'dashboard';
        }
        return { type: 'navigate', target };
      }
      break;
    case 'explain':
      if (typeof data.topic === 'string') {
        return { type: 'explain', topic: data.topic };
      }
      break;
    case 'ask_clarify':
      if (typeof data.question === 'string') {
        return { type: 'ask_clarify', question: data.question };
      }
      break;
    case 'open_newsfeed':
      return { type: 'open_newsfeed' };
    case 'open_inbox_tab':
      if (typeof data.tab === 'string') {
        return { type: 'open_inbox_tab', tab: data.tab };
      }
      break;
    case 'open_connectieflow':
      return { type: 'open_connectieflow' };
    case 'show_updates':
      return { type: 'show_updates' };
    case 'connectieflow_call':
      return { 
        type: 'connectieflow_call', 
        connectionId: typeof data.connectionId === 'string' ? data.connectionId : undefined 
      };
    case 'connectieflow_schedule':
      return { 
        type: 'connectieflow_schedule',
        connectionId: typeof data.connectionId === 'string' ? data.connectionId : undefined 
      };
    case 'connectieflow_pause':
      return { 
        type: 'connectieflow_pause',
        connectionId: typeof data.connectionId === 'string' ? data.connectionId : undefined 
      };
    case 'schedule_availability':
      // schedule_availability accepteert payload met optionele velden
      // Defaults worden later toegepast in getIntent()
      const p = data.payload && typeof data.payload === 'object' ? data.payload : {};
      return {
        type: 'schedule_availability',
        payload: {
          title: typeof p.title === 'string' ? p.title : undefined,
          description: typeof p.description === 'string' ? p.description : undefined,
          date: typeof p.date === 'string' ? p.date : undefined,
          startTime: typeof p.startTime === 'string' ? p.startTime : undefined,
          endTime: typeof p.endTime === 'string' ? p.endTime : undefined,
          repeat: typeof p.repeat === 'string' ? p.repeat : undefined,
          repeatEndDate: typeof p.repeatEndDate === 'string' ? p.repeatEndDate : undefined,
          reminderEnabled: typeof p.reminderEnabled === 'boolean' ? p.reminderEnabled : undefined,
          reminderMinutesBefore: typeof p.reminderMinutesBefore === 'number' ? p.reminderMinutesBefore : undefined,
          locationPreference: typeof p.locationPreference === 'string' ? p.locationPreference : undefined,
          locationDetail: typeof p.locationDetail === 'string' ? p.locationDetail : undefined,
        }
      };
      break;
    case 'idle':
      return { type: 'idle' };
  }

  return null;
}

function extractJSON(text: string): any {
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  
  try {
    return JSON.parse(jsonMatch[0]);
  } catch {
    return null;
  }
}

export const useAIIntent = (): UseAIIntentReturn => {
  const [intent, setIntent] = useState<AIIntent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastTranscriptRef = useRef<string>('');
  const lastTranscriptTimeRef = useRef<number>(0);

  const getIntent = useCallback(async (transcript: string): Promise<AIIntent | null> => {
    if (!transcript.trim()) return null;

    setIsLoading(true);
    setIntent(null);

    // Helper om transcript op te slaan voor context (tenzij het alleen een trigger woord is)
    const saveTranscriptForContext = (text: string) => {
      const isTriggerOnly = /^\s*(plan|plannen|afspraak\s+maken?|opslaan|bewaar)\s*$/i.test(text);
      if (!isTriggerOnly) {
        lastTranscriptRef.current = text;
        lastTranscriptTimeRef.current = Date.now();
      }
    };

    try {
      const lowerTranscript = transcript.toLowerCase().trim();
      
      // ============================================
      // INSTANT PATTERN MATCHING (geen API call!)
      // ============================================
      
      // Navigatie commando's (met optionele tussenwoorden zoals "de/het/mijn")
      const optArticle = '(?:de\\s+|het\\s+|naar\\s+de\\s+|naar\\s+het\\s+|mijn\\s+)?';

      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}(dashboard|home|thuispagina|hoofdpagina|home pagina)\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'dashboard' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}(connecties|netwerk|netwerkpagina)\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'connecties' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}(agenda|kalender)\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'agenda' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}(inbox|berichten)\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'inbox' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}(mijn verhaal|profiel|mijn profiel|verhaal)\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'mijn-verhaal' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      if (new RegExp(`\\b(ga naar|open|navigeer(?: naar)?|toon|laat|bekijk)\\s+${optArticle}vibes\\b`, 'i').test(lowerTranscript)) {
        const intent: AIIntent = { type: 'navigate', target: 'vibes' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      // Nieuwsfeed
      if (/\b(open|ga naar|toon|laat|bekijk)\s+(nieuwsfeed|newsfeed|nieuws feed|nieuws|feed|de feed)\b/i.test(lowerTranscript)) {
        const intent: AIIntent = { type: 'open_newsfeed' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      // ConnectieFlow
      if (/\b(open|start|ga naar)\s+connectieflow\b/i.test(lowerTranscript)) {
        const intent: AIIntent = { type: 'open_connectieflow' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      // Updates
      if (/\b(ja|ja graag|updates|laat updates|wat zijn de updates|begin met updates)\b/i.test(lowerTranscript)) {
        const intent: AIIntent = { type: 'show_updates' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      // ConnectieFlow acties
      if (/\b(bel|bellen|direct bellen|start gesprek|bel nu)\b/i.test(lowerTranscript)) {
        const intent: AIIntent = { type: 'connectieflow_call' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      const tryBuildSchedulePayload = (text: string) => {
        const monthMap: Record<string, number> = {
          januari: 0, jan: 0,
          februari: 1, feb: 1,
          maart: 2, mrt: 2,
          april: 3, apr: 3,
          mei: 4,
          juni: 5, jun: 5,
          juli: 6, jul: 6,
          augustus: 7, aug: 7,
          september: 8, sep: 8,
          oktober: 9, okt: 9,
          november: 10, nov: 10,
          december: 11, dec: 11
        };
        const relativeRegex = /\b(vandaag|morgen|overmorgen)\b/i;
        const dateRegex = /(\d{1,2})\s+(januari|februari|maart|april|mei|juni|juli|augustus|september|oktober|november|december|jan|feb|mrt|apr|aug|sep|okt|nov|dec)(?:\s+(\d{4}))?/i;
        const timeRangeRegex = /(\d{1,2})(?::(\d{2}))?\s*uur.*?(\d{1,2})(?::(\d{2}))?\s*uur/i;
        const timeSingleRegex = /(\d{1,2})(?::(\d{2}))?\s*uur/i;
        const amRegex = /(ochtend|ochtends|am)/i;
        const pmRegex = /(middag|av(ond|onds)|pm)/i;

        const lower = text.toLowerCase();
        let isoDate: string | undefined;
        // Relative date handling
        const rel = text.match(relativeRegex);
        if (rel) {
          const today = new Date();
          const d = new Date(today);
          if (/overmorgen/i.test(rel[0])) d.setDate(d.getDate() + 2);
          else if (/morgen/i.test(rel[0])) d.setDate(d.getDate() + 1);
          // "vandaag" → today
          isoDate = d.toISOString().split('T')[0];
        }
        const m = text.match(dateRegex);
        if (m) {
          const day = parseInt(m[1], 10);
          const monthKey = m[2].toLowerCase();
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Vandaag 00:00 local time
          let year = m[3] ? parseInt(m[3], 10) : now.getFullYear();
          const month = monthMap[monthKey];
          if (!isNaN(day) && month !== undefined && day >= 1 && day <= 31) {
            // Maak datum in local timezone (niet UTC) voor vergelijking
            let d = new Date(year, month, day);
            // Als de datum in het verleden ligt en er geen jaar is genoemd, schuif naar volgend jaar
            if (!m[3] && d.getTime() < today.getTime()) {
              year += 1;
              d = new Date(year, month, day);
            }
            // Converteer naar YYYY-MM-DD formaat
            isoDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }

        const toTime = (h: number, m?: number, isPm?: boolean, isAm?: boolean) => {
          let hour = h;
          if (hour <= 12) {
            if (isPm && hour < 12) hour += 12;
            if (isAm && hour === 12) hour = 0;
          }
          const minutes = m ?? 0;
          const hh = String(hour).padStart(2, '0');
          const mm = String(minutes).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        let startTime: string | undefined;
        let endTime: string | undefined;
        const range = text.match(timeRangeRegex);
        const hasPm = pmRegex.test(text);
        const hasAm = amRegex.test(text);
        if (range) {
          const h1 = parseInt(range[1], 10);
          const m1 = range[2] ? parseInt(range[2], 10) : 0;
          const h2 = parseInt(range[3], 10);
          const m2 = range[4] ? parseInt(range[4], 10) : 0;
          startTime = toTime(h1, m1, hasPm, hasAm);
          endTime = toTime(h2, m2, hasPm, hasAm);
        } else {
          const single = text.match(timeSingleRegex);
          if (single) {
            const h = parseInt(single[1], 10);
            const m2 = single[2] ? parseInt(single[2], 10) : 0;
            startTime = toTime(h, m2, hasPm, hasAm);
            endTime = toTime((h + 1) % 24, m2, hasPm, hasAm);
          }
        }

        // Parse herhaling
        let repeat: 'none' | 'daily' | 'weekly' | 'monthly' = 'none';
        if (/\b(dagelijks|elke dag|iedere dag)\b/i.test(text)) {
          repeat = 'daily';
        } else if (/\b(wekelijks|elke week|iedere week)\b/i.test(text)) {
          repeat = 'weekly';
        } else if (/\b(maandelijks|elke maand|iedere maand)\b/i.test(text)) {
          repeat = 'monthly';
        }

        // Parse herinnering minuten
        let reminderMinutes = 60;
        let reminderEnabled = true;
        
        // Check eerst of herinnering expliciet wordt genoemd
        const hasReminderKeyword = /\b(herinnering|reminder|herinner|alarm)\b/i.test(text);
        
        if (hasReminderKeyword) {
          // Als "herinnering aanzetten" of "herinnering uit" wordt genoemd
          if (/\b(herinnering\s+(uit|uitzetten|off))\b/i.test(text)) {
            reminderEnabled = false;
          } else if (/\b(herinnering\s+(aan|aanzetten|on))\b/i.test(text)) {
            reminderEnabled = true;
          }
          
          // Parse de tijd
          const reminderMatch = text.match(/(\d+)\s*(uur|minuten?|min)\s*(van\s*tevoren|vooraf|eerder)/i);
          if (reminderMatch) {
            const num = parseInt(reminderMatch[1], 10);
            const unit = reminderMatch[2].toLowerCase();
            if (unit.startsWith('uur') || unit === 'u') {
              reminderMinutes = num * 60;
            } else {
              reminderMinutes = num;
            }
          }
        }

        // Parse locatie voorkeur
        let locationPreference: string = 'auto_midpoint';
        let locationDetail = '';
        
        if (/\b(bij mij|mijn kantoor|op mijn kantoor|mijn locatie)\b/i.test(text)) {
          locationPreference = 'at_my_office';
        } else if (/\b(bij (de )?ander|hun kantoor|op hun kantoor|andere locatie)\b/i.test(text)) {
          locationPreference = 'at_other_office';
        } else if (/\b(midpoint|middenpunt|halverwege|tussenin)\b/i.test(text)) {
          locationPreference = 'auto_midpoint';
        } else {
          // Check voor specifieke locatie
          const locMatch = text.match(/\b(?:op|in|bij|locatie:?)\s+([A-Z][a-zA-Z\s]+?)(?:\s|,|$)/);
          if (locMatch && locMatch[1].trim().length > 2) {
            locationPreference = 'selected_location';
            locationDetail = locMatch[1].trim();
          }
        }

        const payload: any = {
          title: 'Beschikbaar',
          description: text,
          date: isoDate,
          startTime: startTime ?? '14:00',
          endTime: endTime ?? '15:00',
          repeat,
          reminderEnabled,
          reminderMinutesBefore: reminderMinutes,
          locationPreference,
          locationDetail
        };
        console.log('📅 Date parsing result:', { 
          text: text.slice(0, 100), 
          isoDate, 
          startTime, 
          endTime, 
          repeat, 
          reminderEnabled,
          reminderMinutes,
          locationPreference,
          locationDetail
        });
        return payload;
      };

      if (/\b(plan|plannen|afspraak|maak een afspraak|plan via agenda)\b/i.test(lowerTranscript)) {
        // Check of de huidige transcript voldoende context heeft (datum of tijd)
        const hasDateOrTime = /\b(\d{1,2}\s+(januari|februari|maart|april|mei|juni|juli|augustus|september|oktober|november|december)|vandaag|morgen|overmorgen|\d{1,2}:\d{2}|\d{1,2}\s*uur)\b/i.test(transcript);
        
        // Als geen datum/tijd EN er is een recente vorige transcript (binnen 10 seconden), combineer ze
        let fullTranscript = transcript;
        const timeSinceLastTranscript = Date.now() - lastTranscriptTimeRef.current;
        if (!hasDateOrTime && lastTranscriptRef.current && timeSinceLastTranscript < 10000) {
          fullTranscript = lastTranscriptRef.current + ' ' + transcript;
          console.log('🔗 Combining with previous transcript:', { previous: lastTranscriptRef.current, current: transcript, combined: fullTranscript });
        }
        
        const payload = tryBuildSchedulePayload(fullTranscript);
        console.log('🗓️ PARSED SCHEDULE PAYLOAD:', { transcript: fullTranscript, payload });
        const intent: AIIntent = { type: 'connectieflow_schedule' };
        (intent as any).actionData = payload; // extra data voor connectieflow_schedule
        // Mirrors schedule_availability shape for downstream consumers
        (intent as any).payload = payload;
        setIntent(intent);
        setIsLoading(false);
        
        // Reset last transcript na gebruik
        lastTranscriptRef.current = '';
        lastTranscriptTimeRef.current = 0;
        
        return intent;
      }
      
      if (/\b(pauze|later|afbreken|stop voor nu|pauze en later)\b/i.test(lowerTranscript)) {
        const intent: AIIntent = { type: 'connectieflow_pause' };
        setIntent(intent);
        setIsLoading(false);
        return intent;
      }
      
      // ============================================
      // Geen match? Gebruik AI voor complexe vragen
      // ============================================
      const availabilityMatch = /(beschikbaar(heid)?|zet\s+me\s+beschikbaar|vrij(e)?\s+(slot|tijd|moment)|beschikbaarheid\s+inplannen|zet\s+beschikbaarheid)/i.test(lowerTranscript);

      // Extra: snelle parsing van reminder en locatie uit de transcript
      const parseReminderMinutes = (text: string): number | undefined => {
        // Alleen matchen als "herinnering/reminder/van tevoren/vooraf" in de buurt staat
        const reminderContext = /(herinnering|reminder|van\s+tevoren|vooraf|melding|notificatie|attendering)/i;
        if (!reminderContext.test(text)) {
          return undefined; // Geen reminder context gevonden
        }
        
        // Zoek naar X uur/minuten NA herinnering-keyword
        const hourMatch = text.match(/(?:herinnering|reminder|van\s+tevoren|vooraf|melding).*?(\d+)\s*(uur|hours?)/i);
        if (hourMatch) {
          const val = parseInt(hourMatch[1], 10);
          if (!isNaN(val)) {
            console.log('⏰ Parsed reminder:', val, 'uur =', val * 60, 'minuten');
            return val * 60;
          }
        }
        const minMatch = text.match(/(?:herinnering|reminder|van\s+tevoren|vooraf|melding).*?(\d+)\s*(minuut|minuten|mins?)/i);
        if (minMatch) {
          const val = parseInt(minMatch[1], 10);
          if (!isNaN(val)) {
            console.log('⏰ Parsed reminder:', val, 'minuten');
            return val;
          }
        }
        return undefined;
      };

      type LocationPref = 'auto_midpoint' | 'at_my_office' | 'at_other_office' | 'selected_location';
      const parseLocationPreference = (text: string): { pref?: LocationPref; detail?: string } => {
        console.log('📍 Parsing location from:', text);
        // Bij mij / thuis / op mijn locatie/adres/plek/kantoor
        if (/bij\s+mij(\s+op\s+locatie)?|thuis\b|op\s+mijn\s+(locatie|adres|plek|kantoor)/i.test(text)) {
          console.log('📍 Matched: at_my_office');
          return { pref: 'at_my_office' };
        }
        // Bij hem/haar/ander(e)/de ander(e)/een ander(e) ondernemer
        if (/bij\s+(een\s+|de\s+)?(hem|haar|ander|andere|ander(e)?\s+ondernemer)/i.test(text)) {
          console.log('📍 Matched: at_other_office');
          return { pref: 'at_other_office' };
        }
        if (/middenweg|halverwege|automatisch\s+midden/i.test(text)) {
          console.log('📍 Matched: auto_midpoint');
          return { pref: 'auto_midpoint' };
        }
        // "op <locatie>" → selected_location
        const onMatch = text.match(/\bop\s+([a-z0-9 ,.'"-]+)$/i);
        if (onMatch && onMatch[1]) {
          console.log('📍 Matched: selected_location ->', onMatch[1]);
          return { pref: 'selected_location', detail: onMatch[1].trim() };
        }
        console.log('📍 No match found');
        return {};
      };

      // OpenAI niet geconfigureerd? Val terug en maak availability intent als het om beschikbaarheid gaat
      if (!openAI.isConfigured()) {
        console.warn('⚠️ OpenAI not configured, using fallback');
        if (availabilityMatch) {
          const rem = parseReminderMinutes(lowerTranscript);
          const loc = parseLocationPreference(lowerTranscript);
          const today = new Date().toISOString().split('T')[0];
          const fallback: AIIntent = {
            type: 'schedule_availability',
            payload: {
              title: 'Beschikbaar',
              description: transcript, // Originele transcript
              date: today,
              startTime: '14:00',
              endTime: '15:00',
              repeat: 'none',
              repeatEndDate: '',
              reminderEnabled: true,
              reminderMinutesBefore: rem ?? 60,
              locationPreference: loc.pref ?? 'auto_midpoint',
              locationDetail: loc.detail ?? ''
            }
          };
          setIntent(fallback);
          setIsLoading(false);
          return fallback;
        }
        const fallback: AIIntent = {
          type: 'respond',
          message: `Ik heb gehoord: "${transcript}". OpenAI is niet geconfigureerd.`
        };
        setIntent(fallback);
        setIsLoading(false);
        return fallback;
      }

      const response = await openAI.sendMessage(transcript, SYSTEM_PROMPT);
      const parsedData = extractJSON(response);
      const validatedIntent = validateIntent(parsedData);

      // Enrich availability intent with defaults + transcript as description
      if (validatedIntent && validatedIntent.type === 'schedule_availability') {
        const p = validatedIntent.payload || {};
        // Vandaag als default date
        const today = new Date().toISOString().split('T')[0];
        const rem = p.reminderMinutesBefore ?? parseReminderMinutes(lowerTranscript);
        // ALTIJD eigen parsing uitvoeren (heeft voorrang), dan LLM waarde als fallback
        const parsedLoc = parseLocationPreference(lowerTranscript);
        const loc = parsedLoc.pref
          ? parsedLoc
          : (p.locationPreference
              ? { pref: p.locationPreference as 'auto_midpoint' | 'at_my_office' | 'at_other_office' | 'selected_location' | undefined, detail: p.locationDetail }
              : {});
        validatedIntent.payload = {
          title: p.title ?? 'Beschikbaar',
          description: p.description ?? transcript, // Originele transcript als beschrijving
          date: p.date ?? today,
          startTime: p.startTime ?? '14:00',
          endTime: p.endTime ?? '15:00',
          repeat: p.repeat ?? 'none',
          repeatEndDate: p.repeatEndDate ?? '',
          reminderEnabled: p.reminderEnabled ?? true,
          reminderMinutesBefore: rem ?? 60,
          locationPreference: (loc.pref as any) ?? 'auto_midpoint',
          locationDetail: loc.detail ?? p.locationDetail ?? ''
        };
      }

      if (!validatedIntent) {
        // Fallback: als het om beschikbaarheid gaat, maak een schedule_availability intent
        if (availabilityMatch) {
          const rem = parseReminderMinutes(lowerTranscript);
          const loc = parseLocationPreference(lowerTranscript);
          const today = new Date().toISOString().split('T')[0];
          const fallback: AIIntent = {
            type: 'schedule_availability',
            payload: {
              title: 'Beschikbaar',
              description: transcript, // Originele transcript
              date: today,
              startTime: '14:00',
              endTime: '15:00',
              repeat: 'none',
              repeatEndDate: '',
              reminderEnabled: true,
              reminderMinutesBefore: rem ?? 60,
              locationPreference: (loc.pref as any) ?? 'auto_midpoint',
              locationDetail: loc.detail ?? ''
            }
          };
          setIntent(fallback);
          setIsLoading(false);
          return fallback;
        }
        // Anders: respond met AI response
        const fallback: AIIntent = {
          type: 'respond',
          message: response
        };
        setIntent(fallback);
        setIsLoading(false);
        return fallback;
      }

      setIntent(validatedIntent);
      setIsLoading(false);
      
      // Sla transcript op voor context bij volgende commando
      saveTranscriptForContext(transcript);
      
      return validatedIntent;

    } catch (error) {
      console.error('❌ AI Intent error:', error);
      const fallback: AIIntent = {
        type: 'respond',
        message: 'Sorry, ik kon je vraag niet verwerken.'
      };
      setIntent(fallback);
      setIsLoading(false);
      
      // Ook hier transcript opslaan voor context
      saveTranscriptForContext(transcript);
      
      return fallback;
    }
  }, []);

  const clearIntent = useCallback(() => {
    setIntent(null);
  }, []);

  return {
    intent,
    isLoading,
    getIntent,
    clearIntent
  };
};

